import type { AraWorkspace } from '@/features/ara-generation/types/ara-workspace'
import { emptyWorkspace } from '@/features/ara-generation/types/ara-workspace'

export const ARA_WORKSPACE_DB_NAME = 'ara-generation-workspace'
export const ARA_WORKSPACE_DB_VERSION = 1
export const ARA_WORKSPACE_STORE = 'workspaces'

export type AraWorkspaceDb = {
  getWorkspace: (assessmentId: string) => Promise<AraWorkspace | null>
  putWorkspace: (workspace: AraWorkspace) => Promise<void>
  deleteWorkspace: (assessmentId: string) => Promise<void>
  clearAll: () => Promise<void>
}

type MemoryBackend = Map<string, AraWorkspace>

function cloneWorkspace(workspace: AraWorkspace): AraWorkspace {
  return structuredClone(workspace)
}

export function createMemoryWorkspaceDb(seed?: MemoryBackend): AraWorkspaceDb {
  const store = seed ?? new Map<string, AraWorkspace>()

  return {
    async getWorkspace(assessmentId) {
      const value = store.get(assessmentId)
      return value ? cloneWorkspace(value) : null
    },
    async putWorkspace(workspace) {
      store.set(workspace.assessmentId, cloneWorkspace(workspace))
    },
    async deleteWorkspace(assessmentId) {
      store.delete(assessmentId)
    },
    async clearAll() {
      store.clear()
    }
  }
}

function openIndexedDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(ARA_WORKSPACE_DB_NAME, ARA_WORKSPACE_DB_VERSION)
    request.onerror = () => reject(request.error ?? new Error('Failed to open ARA workspace DB'))
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(ARA_WORKSPACE_STORE)) {
        db.createObjectStore(ARA_WORKSPACE_STORE, { keyPath: 'assessmentId' })
      }
    }
    request.onsuccess = () => resolve(request.result)
  })
}

function idbRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'))
    request.onsuccess = () => resolve(request.result)
  })
}

export function createIndexedDbWorkspaceDb(): AraWorkspaceDb {
  return {
    async getWorkspace(assessmentId) {
      const db = await openIndexedDb()
      try {
        const tx = db.transaction(ARA_WORKSPACE_STORE, 'readonly')
        const store = tx.objectStore(ARA_WORKSPACE_STORE)
        const value = await idbRequest<AraWorkspace | undefined>(store.get(assessmentId))
        return value ?? null
      } finally {
        db.close()
      }
    },
    async putWorkspace(workspace) {
      const db = await openIndexedDb()
      try {
        const tx = db.transaction(ARA_WORKSPACE_STORE, 'readwrite')
        const store = tx.objectStore(ARA_WORKSPACE_STORE)
        await idbRequest(store.put(workspace))
      } finally {
        db.close()
      }
    },
    async deleteWorkspace(assessmentId) {
      const db = await openIndexedDb()
      try {
        const tx = db.transaction(ARA_WORKSPACE_STORE, 'readwrite')
        const store = tx.objectStore(ARA_WORKSPACE_STORE)
        await idbRequest(store.delete(assessmentId))
      } finally {
        db.close()
      }
    },
    async clearAll() {
      const db = await openIndexedDb()
      try {
        const tx = db.transaction(ARA_WORKSPACE_STORE, 'readwrite')
        const store = tx.objectStore(ARA_WORKSPACE_STORE)
        await idbRequest(store.clear())
      } finally {
        db.close()
      }
    }
  }
}

export function sanitizeWorkspaceForPersistence(workspace: AraWorkspace): AraWorkspace {
  return {
    ...workspace,
    variants: workspace.variants.map(variant => {
      if (variant.phase !== 'streaming') {
        return {
          ...variant,
          edit: variant.edit
            ? {
                ...variant.edit,
                streaming: false,
                previewHtml: null,
                echoRender: null,
                focusedSectionKey: null,
                selectedElementTarget: null
              }
            : null
        }
      }
      return {
        ...variant,
        phase: 'error',
        stage: 'preparing',
        error: 'Generation interrupted',
        backboneStatus: '',
        edit: null
      }
    }),
    showConfigure: workspace.variants.length === 0 ? true : workspace.showConfigure
  }
}

export function workspaceOrEmpty(
  assessmentId: string,
  workspace: AraWorkspace | null | undefined
): AraWorkspace {
  return workspace ?? emptyWorkspace(assessmentId)
}
